import type {
  Impact,
  ResolutionCode,
  Status,
  TicketType,
  TimelineItem,
  Urgency,
} from "../api/types";

export const STATUS_LABELS: Record<Status, string> = {
  new: "New",
  triage: "Reviewing",
  assigned: "Assigned",
  in_progress: "In progress",
  pending_requester: "Waiting for requester",
  pending_third_party: "Waiting for third party",
  pending_internal: "Waiting internally",
  resolved: "Resolved",
  closed: "Closed",
};

export type Tone = "neutral" | "primary" | "success" | "warning" | "danger";

export const STATUS_TONES: Record<Status, Tone> = {
  new: "primary",
  triage: "primary",
  assigned: "neutral",
  in_progress: "neutral",
  pending_requester: "warning",
  pending_third_party: "warning",
  pending_internal: "warning",
  resolved: "success",
  closed: "neutral",
};

export const OPEN_STATUSES: Status[] = [
  "new",
  "triage",
  "assigned",
  "in_progress",
  "pending_requester",
  "pending_third_party",
  "pending_internal",
];
export const DONE_STATUSES: Status[] = ["resolved", "closed"];

const PENDING: Status[] = ["pending_requester", "pending_third_party", "pending_internal"];

/**
 * Mirrors MANUAL_TRANSITIONS in backend/app/tickets/rules.py (the backend enforces it).
 * Assigning is optional: work can start, or the ticket be resolved, straight from the queue.
 */
export const NEXT_STATUSES: Record<Status, Status[]> = {
  new: ["triage", "in_progress", ...PENDING],
  triage: ["in_progress", ...PENDING],
  assigned: ["in_progress", ...PENDING],
  in_progress: [...PENDING],
  pending_requester: ["in_progress", "pending_third_party", "pending_internal"],
  pending_third_party: ["in_progress", "pending_requester", "pending_internal"],
  pending_internal: ["in_progress", "pending_requester", "pending_third_party"],
  resolved: [],
  closed: [],
};

export const PRIORITY_LABELS: Record<number, string> = {
  1: "P1 Critical",
  2: "P2 High",
  3: "P3 Normal",
  4: "P4 Low",
};

export const PRIORITY_TONES: Record<number, Tone> = { 1: "danger", 2: "warning", 3: "neutral", 4: "neutral" };

export const TYPE_LABELS: Record<TicketType, string> = {
  incident: "Incident",
  service_request: "Service request",
};

/** Impact: how far the problem reaches. The term, and what it means. */
export const IMPACT_LABELS: Record<Impact, string> = {
  single: "Individual",
  multiple: "Team",
  widespread: "Organisation",
};

export const IMPACT_DETAILS: Record<Impact, string> = {
  single: "One person",
  multiple: "A team or several people",
  widespread: "A site or the whole company",
};

/** Urgency: how soon it needs attention. */
export const URGENCY_LABELS: Record<Urgency, string> = {
  high: "High",
  medium: "Medium",
  low: "Low",
};

/** What each urgency means for something broken. */
export const URGENCY_DETAILS: Record<Urgency, string> = {
  high: "Work has stopped",
  medium: "Work is slower or harder",
  low: "No immediate effect",
};

/** What each urgency means for a service request: how soon, not how broken. */
export const URGENCY_DETAILS_REQUEST: Record<Urgency, string> = {
  high: "Needed to work",
  medium: "Needed soon",
  low: "No rush",
};

const MATRIX: Record<Impact, Record<Urgency, number>> = {
  widespread: { high: 1, medium: 2, low: 3 },
  multiple: { high: 2, medium: 3, low: 4 },
  single: { high: 3, medium: 4, low: 4 },
};

/** Mirrors suggested_priority in backend/app/tickets/rules.py, for a live preview. */
export function suggestedPriority(impact: Impact, urgency: Urgency): number {
  return MATRIX[impact][urgency];
}

/**
 * What the requester is asked once a ticket is resolved: the button, and the line under the
 * resolution. Only a real fix asks "is it fixed?"; a cancelled ticket just closes.
 */
export const CLOSE_WORDING: Record<ResolutionCode, { button: string; prompt: string }> = {
  fixed: { button: "Confirm it's fixed", prompt: "Is it fixed? Confirm above, or reopen it if not." },
  workaround: {
    button: "Confirm it works",
    prompt: "Is the workaround working for you? Confirm above, or reopen it if not.",
  },
  request_fulfilled: { button: "Confirm it's done", prompt: "Is it all done? Confirm above, or reopen it if not." },
  not_reproducible: {
    button: "Close the ticket",
    prompt: "We couldn't make the problem happen again. If it comes back, reopen the ticket.",
  },
  cancelled: { button: "Close the ticket", prompt: "This ticket was cancelled. Reopen it if you still need help." },
  duplicate: {
    button: "Close the ticket",
    prompt: "This is being handled on the other ticket. Reopen it if this is a different problem.",
  },
};

export const RESOLUTION_LABELS: Record<ResolutionCode, string> = {
  fixed: "Fixed",
  workaround: "Workaround in place",
  not_reproducible: "Couldn't reproduce",
  request_fulfilled: "Request fulfilled",
  cancelled: "Cancelled",
  duplicate: "Duplicate",
};

export function resolutionCodesFor(type: TicketType): ResolutionCode[] {
  return type === "incident"
    ? ["fixed", "workaround", "not_reproducible", "cancelled"]
    : ["request_fulfilled", "cancelled"];
}

function name(value: unknown): string {
  return typeof value === "object" && value !== null && "name" in value ? String((value as { name: unknown }).name) : "nobody";
}

function statusLabel(value: unknown): string {
  return STATUS_LABELS[value as Status] ?? String(value);
}

/** One line describing a timeline event, e.g. "assigned this to Dan Dev (Development)". */
export function describeEvent(item: TimelineItem): string {
  const d = item.data;
  switch (item.kind) {
    case "created": {
      const from = d.email_from as { name?: string; email?: string } | undefined;
      const raised = d.on_behalf_of
        ? `raised this for ${name(d.on_behalf_of)}`
        : from?.email
          ? `raised this from an email by ${from.name && from.name !== from.email ? `${from.name} (${from.email})` : from.email}`
          : "raised this ticket";
      const shared = Array.isArray(d.shared_with) ? d.shared_with.map(name) : [];
      return shared.length ? `${raised} and shared it with ${shared.join(", ")}` : raised;
    }
    case "assigned": {
      const owner = d.to_owner
        ? d.to_group
          ? `${name(d.to_owner)} (${name(d.to_group)})`
          : name(d.to_owner)
        : d.to_group
          ? `the ${name(d.to_group)} group`
          : "nobody";
      // "Moved" only when it changes hands; a first owner on a routed ticket is an assignment.
      const moved = Boolean(d.from_owner) || (Boolean(d.from_group) && name(d.from_group) !== name(d.to_group));
      return moved ? `moved this to ${owner}` : `assigned this to ${owner}`;
    }
    case "status_changed":
      return d.automatic
        ? `status changed to ${statusLabel(d.to)} after the requester replied`
        : `changed the status from ${statusLabel(d.from)} to ${statusLabel(d.to)}`;
    case "resolved":
      return `resolved this: ${RESOLUTION_LABELS[d.code as ResolutionCode] ?? String(d.code)}`;
    case "closed":
      return d.by === "system"
        ? "closed automatically after the waiting period"
        : d.by === "requester"
          ? "confirmed the fix and closed this"
          : "closed this";
    case "reopened":
      return "reopened this";
    case "priority_changed": {
      const to = d.to as { priority?: number } | undefined;
      return `set the priority to ${PRIORITY_LABELS[to?.priority ?? 0] ?? "?"}`;
    }
    case "classification_changed":
      return `changed the service to ${d.service ? String(d.service) : "not sure"}${d.category ? ` › ${String(d.category)}` : ""}`;
    case "watcher_added":
      return `shared this with ${name(d.user)}`;
    case "watcher_removed":
      return `removed ${name(d.user)} from this ticket`;
    case "tags_changed": {
      const added = (d.added as string[] | undefined) ?? [];
      const removed = (d.removed as string[] | undefined) ?? [];
      return [added.length ? `added tag ${added.join(", ")}` : "", removed.length ? `removed tag ${removed.join(", ")}` : ""]
        .filter(Boolean)
        .join("; ");
    }
    case "restricted_changed":
      return d.to ? "marked this confidential" : "made this visible to all support staff";
    case "duplicate_linked":
      return `marked this as a duplicate of ${String(d.original)}`;
    case "duplicate_added":
      return `marked ${String(d.duplicate)} as a duplicate of this`;
    case "attachment_added":
      return `attached ${String(d.filename)}`;
    case "attachments_refused":
      return `couldn't attach ${((d.filenames as string[] | undefined) ?? []).join(", ")} (type not allowed)`;
    default:
      return item.kind.replace(/_/g, " ");
  }
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export const SOURCE_LABELS: Record<string, string> = {
  portal: "Support desk",
  email: "Email",
  in_app: "App Support link",
  chat: "Chat",
};

export const ENVIRONMENT_LABELS: Record<string, string> = {
  production: "Live",
  staging: "Staging",
  test: "Test",
  development: "Development",
};
